import {
  CandidateItemDto,
  type CandidateSearchRequestDto,
  type CandidateSortRequestDto,
  CandidatesSearchResponseDto,
  DictionaryTypeDto,
  GetCandidatesSortPropertyDto,
  QuizScoreDto,
  SortDirectionDto,
} from '@/api/generated';
import { useGetAllDictionaries } from '@/api/requests/useGetAllDictionaries.util.ts';
import { TablePagination } from '@/components/table/TablePagination.comp.tsx';
import { useActiveAreaOfInterests } from '@/hooks/useActiveAreaOfInterests.ts';
import { getAppLanguage } from '@/i18n.ts';
import { useUserContext } from '@/utils/providers/userContext/useUserContext.tsx';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';
import { CandidateProfilePanel } from '@/views/admin/candidates/CandidateProfilePanel.comp.tsx';
import { CandidatesTable } from '@/views/admin/candidates/CandidatesTable.comp.tsx';
import {
  countFor,
  parseSearch,
  QUICK_FILTER_ORDER,
  QUICK_FILTERS,
  type QuickFilter,
} from '@/views/admin/candidates/candidateStatus.ts';
import { useGetCandidateStatusCounts } from '@/views/admin/candidates/useCandidatesApi.util.ts';
import { COLORS, formatNumber } from '@/views/admin/dashboard/dashboardTheme.ts';
import { useGetCandidates } from '@/views/admin/util/useGetCandidates.util.ts';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { IoSearchOutline } from 'react-icons/io5';

const SEARCH_DELAY_MS = 400;

interface Filters {
  area: string;
  decisionFrom: string;
  decisionTo: string;
  location: string;
  quickFilter: QuickFilter;
  score: '' | QuizScoreDto;
  search: string;
}

const NO_FILTERS: Filters = {
  area: '',
  decisionFrom: '',
  decisionTo: '',
  location: '',
  quickFilter: 'all',
  score: '',
  search: '',
};

const EMPTY_RESULTS: CandidatesSearchResponseDto = {
  currentPage: 0,
  items: [],
  totalPages: 0,
  totalResults: 0,
};

const selectStyle: React.CSSProperties = {
  backgroundColor: '#fff',
  border: '1px solid #ccc',
  color: COLORS.ink,
  fontSize: 14,
  height: 38,
  padding: '0 10px',
};

export const CandidatesTab: React.FC = () => {
  const { t } = useTranslationWithPrefix('views.admin');
  const { user } = useUserContext();
  const language = getAppLanguage();
  const { getCandidates, isGetting } = useGetCandidates();
  const { activeAreaOfInterests } = useActiveAreaOfInterests();
  const { dictionaries } = useGetAllDictionaries();
  const { counts, refetchCounts } = useGetCandidateStatusCounts(user?.userId ?? '');
  const [filters, setFilters] = useState<Filters>(NO_FILTERS);
  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [sort, setSort] = useState<CandidateSortRequestDto>({
    direction: SortDirectionDto.Desc,
    property: GetCandidatesSortPropertyDto.QuizFinishedAt,
  });
  const [results, setResults] = useState<CandidatesSearchResponseDto>(EMPTY_RESULTS);
  const [selected, setSelected] = useState<CandidateItemDto>();
  // the "time left" chips are relative to the moment the list was loaded
  const [now, setNow] = useState(() => new Date());

  const request = useMemo<CandidateSearchRequestDto>(() => {
    const criteria = QUICK_FILTERS[filters.quickFilter];
    const area = activeAreaOfInterests?.find(candidate => candidate.name === filters.area);
    return {
      ...parseSearch(filters.search),
      adminActionPossibleBeforeFrom: filters.decisionFrom
        ? `${filters.decisionFrom}T00:00:00Z`
        : undefined,
      adminActionPossibleBeforeTo: filters.decisionTo
        ? `${filters.decisionTo}T23:59:59Z`
        : undefined,
      adminStatuses: criteria.adminStatuses,
      areaOfInterests: area ? [area] : undefined,
      isFromFair: criteria.isFromFair,
      locationIds: filters.location ? [Number(filters.location)] : undefined,
      quizScore: filters.score || undefined,
      sort,
      statuses: criteria.statuses,
    };
  }, [activeAreaOfInterests, filters, sort]);

  const search = useCallback(async () => {
    if (!user) {
      return;
    }
    const found = await getCandidates({ adminUserId: user.userId, dto: request, page, pageSize });
    setResults(found);
    setNow(new Date());
    // the selected candidate's row is refreshed too, e.g. after an admin action
    setSelected(
      current => found.items.find(item => item.candidateId === current?.candidateId) ?? current,
    );
  }, [getCandidates, page, pageSize, request, setNow, setResults, setSelected, user]);

  useEffect(() => {
    void search();
  }, [search]);

  useEffect(() => {
    const timeout = setTimeout(
      () =>
        setFilters(current =>
          current.search === searchText ? current : { ...current, search: searchText },
        ),
      SEARCH_DELAY_MS,
    );
    return () => clearTimeout(timeout);
  }, [searchText]);

  const changeFilter = <K extends keyof Filters>(key: K, value: Filters[K]) => {
    setPage(0);
    setFilters(current => ({ ...current, [key]: value }));
  };

  const clearFilters = () => {
    setPage(0);
    setSearchText('');
    setFilters(NO_FILTERS);
  };

  const onActionSuccess = async () => {
    await search();
    void refetchCounts();
  };

  const hasFilters = JSON.stringify(filters) !== JSON.stringify(NO_FILTERS) || searchText !== '';
  const locations = dictionaries?.[DictionaryTypeDto.Location] ?? [];

  return (
    <div style={{ backgroundColor: '#f4f4f4', margin: '-1.25rem -1.6875rem', minHeight: '100%' }}>
      <div className="flex flex-col gap-4 p-4 lg:p-6">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="m-0 text-2xl font-bold" style={{ color: COLORS.ink }}>
              {t('candidates.title')}
            </h1>
          </div>
          <div className="relative w-full max-w-[420px]">
            <IoSearchOutline
              aria-hidden
              className="absolute top-1/2 left-3 -translate-y-1/2"
              color={COLORS.inkMuted}
            />
            <input
              aria-label={t('candidates.search')}
              className="w-full"
              onChange={event => {
                setPage(0);
                setSearchText(event.target.value);
              }}
              placeholder={t('candidates.search')}
              style={{ ...selectStyle, height: 42, paddingLeft: 36, width: '100%' }}
              type="search"
              value={searchText}
            />
          </div>
        </div>

        <div aria-label={t('candidates.title')} className="flex flex-wrap gap-2" role="tablist">
          {QUICK_FILTER_ORDER.map(filter => {
            const active = filters.quickFilter === filter;
            return (
              <button
                aria-selected={active}
                className="inline-flex cursor-pointer items-center gap-2 px-3 py-2 text-sm font-bold"
                key={filter}
                onClick={() => changeFilter('quickFilter', filter)}
                role="tab"
                style={{
                  backgroundColor: active ? COLORS.ink : '#fff',
                  border: `1px solid ${active ? COLORS.ink : '#ddd'}`,
                  color: active ? '#fff' : COLORS.ink,
                }}
                type="button"
              >
                {t(`candidates.quickFilters.${filter}`)}
                <span
                  className="px-1.5 text-xs"
                  style={{
                    backgroundColor: active ? COLORS.orange : '#f1f1f1',
                    borderRadius: 10,
                    color: COLORS.ink,
                  }}
                >
                  {formatNumber(countFor(filter, counts), language)}
                </span>
              </button>
            );
          })}
        </div>

        <section className="bg-white" style={{ border: '1px solid #e5e5e5' }}>
          <div
            className="flex flex-wrap items-center gap-2 px-4 py-3"
            style={{ borderBottom: `1px solid ${COLORS.grid}` }}
          >
            <select
              aria-label={t('candidates.filters.score')}
              onChange={event => changeFilter('score', event.target.value as Filters['score'])}
              style={selectStyle}
              value={filters.score}
            >
              <option value="">{t('candidates.filters.score')}</option>
              {Object.values(QuizScoreDto).map(score => (
                <option key={score} value={score}>
                  {t(`table.quizScores.${score}`)}
                </option>
              ))}
            </select>
            <select
              aria-label={t('candidates.filters.area')}
              onChange={event => changeFilter('area', event.target.value)}
              style={selectStyle}
              value={filters.area}
            >
              <option value="">{t('candidates.filters.area')}</option>
              {activeAreaOfInterests?.map(area => (
                <option key={area.areaOfInterestId} value={area.name}>
                  {area.description}
                </option>
              ))}
            </select>
            <select
              aria-label={t('candidates.filters.location')}
              onChange={event => changeFilter('location', event.target.value)}
              style={selectStyle}
              value={filters.location}
            >
              <option value="">{t('candidates.filters.location')}</option>
              {locations.map(location => (
                <option key={location.identifier?.id} value={location.identifier?.id}>
                  {location.label?.value}
                </option>
              ))}
            </select>
            <label
              className="inline-flex items-center gap-2 text-sm"
              style={{ color: COLORS.inkSecondary }}
            >
              {t('candidates.filters.decisionFrom')}
              <input
                onChange={event => changeFilter('decisionFrom', event.target.value)}
                style={selectStyle}
                type="date"
                value={filters.decisionFrom}
              />
            </label>
            <label
              className="inline-flex items-center gap-2 text-sm"
              style={{ color: COLORS.inkSecondary }}
            >
              {t('candidates.filters.decisionTo')}
              <input
                onChange={event => changeFilter('decisionTo', event.target.value)}
                style={selectStyle}
                type="date"
                value={filters.decisionTo}
              />
            </label>
            {hasFilters && (
              <button
                className="cursor-pointer border-0 bg-transparent px-2 text-sm font-bold underline"
                onClick={clearFilters}
                style={{ color: COLORS.ink }}
                type="button"
              >
                {t('candidates.filters.clear')}
              </button>
            )}
            <span className="ml-auto text-sm" style={{ color: COLORS.inkSecondary }}>
              <strong style={{ color: COLORS.ink }}>
                {formatNumber(results.totalResults, language)}
              </strong>{' '}
              {t('dashboard.map.candidates')}
            </span>
          </div>

          <CandidatesTable
            candidates={results.items}
            isLoading={isGetting}
            language={language}
            now={now}
            onOpen={setSelected}
            onSort={next => {
              setPage(0);
              setSort(next);
            }}
            selectedId={selected?.candidateId}
            sort={sort}
          />

          <div className="px-4 pb-3">
            <TablePagination
              onNextPage={() =>
                setPage(current => Math.min(current + 1, Math.max(results.totalPages - 1, 0)))
              }
              onPageChange={setPage}
              onPageSizeChange={event => {
                setPage(0);
                setPageSize(Number(event.target.value));
              }}
              onPreviousPage={() => setPage(current => Math.max(current - 1, 0))}
              page={page}
              pageSize={pageSize}
              totalPages={results.totalPages}
              totalResults={results.totalResults}
            />
          </div>
        </section>
      </div>

      {selected && (
        <CandidateProfilePanel
          candidate={selected}
          language={language}
          onActionSuccess={onActionSuccess}
          onClose={() => setSelected(undefined)}
        />
      )}
    </div>
  );
};
